import {
  Save, CheckCircle2, AlertCircle, User, Phone, Globe, Calendar,
  ShieldAlert, Trash2, Loader2, Mail,
} from 'lucide-react';
import { GRAD, fg, fgMuted, border, surface, muted, Field, InputStyle } from './profile-shared';

interface ProfileTabProps {
  fileRef: React.RefObject<HTMLInputElement | null>;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  setName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  age: string;
  setAge: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  bio: string;
  setBio: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  success: string;
  setSuccess: (v: string) => void;
  saving: boolean;
  emailErr: string;
  setEmailErr: (v: string) => void;
  emailMsg: string;
  setEmailMsg: (v: string) => void;
  emailSaving: boolean;
  emailChanged: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onUpdateEmail: (e: React.FormEvent) => void;
  onOpenDelete: () => void;
}

export default function ProfileTab({
  fileRef, onAvatarChange,
  name, setName, email, setEmail, phone, setPhone, age, setAge, website, setWebsite, bio, setBio,
  error, setError, success, setSuccess, saving,
  emailErr, setEmailErr, emailMsg, setEmailMsg, emailSaving, emailChanged,
  onSubmit, onUpdateEmail, onOpenDelete,
}: ProfileTabProps) {
  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        {/* Avatar upload (hidden) */}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarChange} />

        {/* Photo card */}
        <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
          <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
            Profile Picture
          </h2>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded-xl px-4 py-2 text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Upload new photo
            </button>
            <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              JPG, PNG or GIF · Max 5 MB
            </p>
          </div>
        </div>

        {/* Details card */}
        <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
          <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
            Account Details
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Full name" icon={<User size={12} />}>
              <InputStyle
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setError(''); setSuccess(''); }}
                placeholder="Your full name"
              />
            </Field>

            <Field label="Email address" icon={<User size={12} />}>
              <InputStyle
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailErr(''); setEmailMsg(''); }}
                placeholder="you@example.com"
                autoComplete="email"
              />
              {emailErr && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs" style={{ color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <AlertCircle size={13} className="mt-px shrink-0" /> {emailErr}
                </p>
              )}
              {emailMsg && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs" style={{ color: '#22c55e', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <CheckCircle2 size={13} className="mt-px shrink-0" /> {emailMsg}
                </p>
              )}
              {emailChanged && !emailMsg && (
                <button
                  type="button"
                  onClick={onUpdateEmail}
                  disabled={emailSaving}
                  className="mt-2 inline-flex items-center gap-1.5 self-start rounded-lg px-3 py-2 text-xs font-bold transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {emailSaving ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                  {emailSaving ? 'Sending…' : 'Update email'}
                </button>
              )}
            </Field>

            <Field label="Phone number" icon={<Phone size={12} />}>
              <InputStyle
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+44 7700 900000"
              />
            </Field>

            <Field label="Age" icon={<Calendar size={12} />}>
              <InputStyle
                type="number"
                value={age}
                onChange={e => setAge(e.target.value)}
                placeholder="e.g. 32"
                min={1}
                max={120}
              />
            </Field>

            <div className="sm:col-span-2">
              <Field label="Website" icon={<Globe size={12} />}>
                <InputStyle
                  type="url"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                  placeholder="https://yourwebsite.com"
                />
              </Field>
            </div>
          </div>
        </div>

        {/* Bio card */}
        <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
          <div className="mb-1 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
              About Me
            </h2>
            <span className="text-xs" style={{ color: bio.length > 450 ? '#ef4444' : fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {bio.length}/500
            </span>
          </div>
          <textarea
            value={bio}
            onChange={e => setBio(e.target.value.slice(0, 500))}
            placeholder="Write a short description about yourself…"
            rows={4}
            className="mt-3 w-full resize-none rounded-xl px-4 py-3 text-sm outline-none transition-all"
            style={{
              background: muted,
              border: `1px solid ${border}`,
              color: fg,
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          />
        </div>

        {/* Feedback */}
        {error && (
          <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm"
            style={{ background: 'hsla(0,80%,60%,0.1)', border: '1px solid hsla(0,80%,60%,0.3)', color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm"
            style={{ background: 'hsla(143,70%,45%,0.1)', border: '1px solid hsla(143,70%,45%,0.3)', color: 'hsl(143 60% 40%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <CheckCircle2 size={15} className="shrink-0" />
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
          style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <Save size={15} />
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>

      {/* Danger zone — account deletion */}
      <div className="mt-5 rounded-2xl p-5" style={{ background: surface, border: '1px solid hsla(0,70%,55%,0.35)' }}>
        <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: 'hsl(0 70% 55%)' }}>
          <ShieldAlert size={15} /> Danger Zone
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Delete my account
            </p>
            <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Permanently removes your account and all associated data. This cannot be undone.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenDelete}
            className="flex shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold transition-all hover:opacity-90"
            style={{ background: 'hsla(0,70%,55%,0.1)', color: 'hsl(0 70% 55%)', border: '1px solid hsla(0,70%,55%,0.3)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <Trash2 size={14} /> Delete Account
          </button>
        </div>
      </div>
    </>
  );
}
